import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const SITE_URL = 'https://hoosierlifeinsurance.com';
const FORM_URL = 'https://readdy.ai/api/form/d74kp8kprit363gl0nig';

const TESTIMONIALS = [
  {
    name: 'Jason & Michelle H.',
    neighborhood: 'Aboite Township, Fort Wayne',
    text: 'I work at Lincoln Financial and assumed the company plan was more than enough. Benjamin showed me I had $900,000 in unmet coverage for my family. A 25-year term costs us $41/month combined. Best money we spend.',
    stars: 5,
    product: 'Term Life Insurance',
  },
  {
    name: 'Carlos R.',
    neighborhood: 'Waynedale, Fort Wayne',
    text: 'GM Assembly worker for 17 years. Union plan is solid but it\'s gone the second I retire. Benjamin locked me into a $500K private policy for $33/month. No matter what happens at the plant, my family is covered.',
    stars: 5,
    product: 'Term Life Insurance',
  },
  {
    name: 'Angela & Travis B.',
    neighborhood: 'New Haven, Allen County',
    text: 'We bought a house near Sweetwater and needed mortgage protection fast. Benjamin got us $650K approved in 48 hours — no exam. He walked us through everything on a Zoom call. Couldn\'t have been easier.',
    stars: 5,
    product: 'Mortgage Protection',
  },
  {
    name: 'Denise K.',
    neighborhood: 'Southwest Fort Wayne',
    text: 'Single mom, three kids, nurse at Parkview. I had zero private coverage. Benjamin found me $500K whole life for $46/month. My kids are protected now — I sleep so much better knowing that.',
    stars: 5,
    product: 'Whole Life Insurance',
  },
];

const FAQS = [
  {
    q: 'How much does life insurance cost for a Fort Wayne family?',
    a: 'Most healthy Fort Wayne adults aged 30–40 pay $22–$48/month for $500,000 in 20-year term coverage. A 35-year-old male in good health pays around $30–$35/month; females pay $25–$29/month. Fort Wayne families with mortgages of $160,000–$240,000 typically need $500K–$900K in coverage to replace income and retire the home debt.',
  },
  {
    q: 'I work at Lincoln Financial — isn\'t my company plan enough coverage?',
    a: 'Almost never. Lincoln Financial\'s group plan covers 1–2× your salary — typically $60,000–$150,000 for most employees. That won\'t replace your income for 20 years, pay off a Fort Wayne mortgage, or fund your children\'s education. A private portable policy starts at around $26/month and locks your rate regardless of your employment status.',
  },
  {
    q: 'What life insurance is best for GM Assembly or Steel Dynamics workers in Fort Wayne?',
    a: 'A 20-year term life policy is the most cost-effective option for Fort Wayne manufacturing workers. It locks in your rate today before any future health changes and stays active regardless of layoffs, plant changes, or retirements. Union plans are valuable but tied to employment — a private policy bought at 35 is yours at a locked rate through age 55 no matter what.',
  },
  {
    q: 'Can I get life insurance in Fort Wayne without a medical exam?',
    a: 'Yes. Simplified issue and accelerated underwriting policies offer same-day or next-day approval for healthy Fort Wayne applicants — no blood draw, no doctor visit. Coverage up to $1M is available. We work with multiple no-exam carriers to find your fastest and most affordable path to coverage.',
  },
  {
    q: 'What ZIP codes in Fort Wayne and Allen County do you serve?',
    a: 'We serve all Fort Wayne and Allen County ZIP codes including 46801–46809, 46814, 46815, 46816, 46818, 46819, 46825, and 46835. We also serve New Haven, Leo-Cedarville, Huntertown, Grabill, Woodburn, and all surrounding Allen County communities. All consultations are remote — phone or Zoom.',
  },
  {
    q: 'Why do Fort Wayne life insurance rates run lower than national averages?',
    a: 'Indiana\'s competitive insurance market, lower cost of living, and favorable actuarial data result in rates that run 8–12% below the national average. As an independent agency, we compare 20+ A-rated carriers to find the lowest available rate for your specific age, health, and coverage needs — bringing Fort Wayne families significant savings vs. single-carrier quotes.',
  },
];

const NEIGHBORHOODS = [
  'Aboite Township', 'Waynedale', 'New Haven', 'Leo-Cedarville',
  'Huntertown', 'Grabill', 'Woodburn', 'Southwest Fort Wayne',
  'Northwest Fort Wayne', 'Northeast Fort Wayne', 'Downtown Fort Wayne',
  'Lakeside Park', 'Bloomingdale', 'Franke Park Area', 'Monroeville', 'Hoagland',
];

const SERVICES = [
  {
    icon: 'ri-shield-check-fill',
    title: 'Term Life Insurance',
    desc: 'Maximum coverage at the lowest cost. Most Fort Wayne families ages 25–50 choose a 20-year term to cover their mortgage and replace income through their peak earning years.',
    cta: 'See Term Life Rates',
    link: '/term-life',
  },
  {
    icon: 'ri-home-heart-fill',
    title: 'Mortgage Protection',
    desc: 'Fort Wayne home values average $160,000–$240,000. This policy pays your mortgage completely if something happens — your family keeps their home with zero forced sale.',
    cta: 'Protect My Home',
    link: '/mortgage-protection',
  },
  {
    icon: 'ri-heart-pulse-fill',
    title: 'Whole Life Insurance',
    desc: 'Permanent coverage that builds cash value year after year. Ideal for Lincoln Financial professionals and Fort Wayne business owners building long-term wealth alongside protection.',
    cta: 'Explore Whole Life',
    link: '/whole-life',
  },
  {
    icon: 'ri-shield-user-fill',
    title: 'No Medical Exam',
    desc: 'Same-day approval, no needles, no waiting room. Popular with Parkview nurses and GM Assembly workers who can\'t schedule a traditional exam during busy shifts.',
    cta: 'Fast Approval',
    link: '/no-medical-exam',
  },
];

const EMPLOYERS = [
  {
    employer: 'Lincoln Financial Group',
    employees: '5,000+ in Fort Wayne metro',
    gap: 'Lincoln Financial\'s own group plan covers 1–2× salary. A $75K employee gets $75K–$150K in coverage — not nearly enough to replace income, pay a Fort Wayne mortgage, and fund kids\' education for 20 years. Even the best group plans leave a gap.',
  },
  {
    employer: 'Parkview Health',
    employees: '9,000+ in Allen County',
    gap: 'Healthcare workers protect patients daily but often underinsure themselves. Parkview\'s group plan is 1–2× salary and ends when employment does. A private policy gives Parkview nurses and staff permanent, portable coverage starting around $26/month.',
  },
  {
    employer: 'General Motors Fort Wayne Assembly',
    employees: '3,800+ in Fort Wayne',
    gap: 'GM UAW union plans are strong but employment-dependent. A plant slowdown, early retirement, or voluntary separation ends your group coverage overnight. A private 20-year term locked at 35 keeps your family covered regardless of what GM does next.',
  },
  {
    employer: 'Sweetwater Sound',
    employees: '2,200+ in Fort Wayne',
    gap: 'Fast-growing company with a passionate workforce — but startup-culture group benefits rarely match what families need long-term. Sweetwater employees should have private portable coverage that doesn\'t depend on the company\'s next funding round or acquisition.',
  },
  {
    employer: 'Steel Dynamics',
    employees: '2,800+ in Allen County',
    gap: 'Heavy manufacturing carries significant workplace risk. Steel Dynamics union plans cover employment — not your family\'s next 20 years. A private policy bought at your current health locks your rate before any future health changes affect your eligibility.',
  },
  {
    employer: 'Fort Wayne Community Schools',
    employees: '4,200+ teachers & staff',
    gap: 'PERF pension covers retirement. It doesn\'t protect your family from today forward. A $500K term policy costs a Fort Wayne teacher approximately $22–$30/month and fills the income-replacement gap that the pension doesn\'t touch.',
  },
];

export default function FortWayneLifeInsurancePage() {
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    coverage_amount: '', age: '', best_time: '', sms_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Life Insurance Fort Wayne Indiana | Compare 20+ Carriers — Hoosier Life Insurance';

    const setMeta = (name: string, content: string, isProp = false) => {
      const attr = isProp ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.content = content;
    };

    setMeta('description', 'Life insurance Fort Wayne, Indiana — independent agent compares 20+ carriers. Serving Lincoln Financial, GM Assembly, Parkview Health & all Allen County families. Free quotes. (317) 648-2983.');
    setMeta('keywords', 'life insurance Fort Wayne Indiana, Fort Wayne IN life insurance, Allen County life insurance, Lincoln Financial employee life insurance, GM Fort Wayne Assembly life insurance, term life insurance Fort Wayne');
    setMeta('geo.region', 'US-IN');
    setMeta('geo.placename', 'Fort Wayne, Indiana');
    setMeta('geo.position', '41.0793;-85.1394');
    setMeta('ICBM', '41.0793, -85.1394');
    setMeta('og:title', 'Life Insurance in Fort Wayne, Indiana | Hoosier Life Insurance', true);
    setMeta('og:description', 'Fort Wayne, IN life insurance — independent agent serving Lincoln Financial, GM Assembly workers, Parkview Health staff & all Allen County families. Free, no-pressure quotes. (317) 648-2983.', true);
    setMeta('og:url', `${SITE_URL}/life-insurance-fort-wayne`, true);
    setMeta('og:type', 'website', true);
    setMeta('twitter:card', 'summary_large_image');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `${SITE_URL}/life-insurance-fort-wayne`;

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${SITE_URL}/life-insurance-fort-wayne#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How much does life insurance cost in Fort Wayne, Indiana?',
          acceptedAnswer: { '@type': 'Answer', text: 'A healthy 35-year-old in Fort Wayne, Indiana pays approximately $30–$35/month for $500,000 in 20-year term life coverage. Males pay around $33/month and females around $28/month at preferred health ratings. Indiana rates run 8–12% below the national average. An independent agent comparing 20+ carriers will find your lowest available rate.' },
        },
        {
          '@type': 'Question',
          name: 'Is Lincoln Financial or Parkview Health group coverage enough for Fort Wayne families?',
          acceptedAnswer: { '@type': 'Answer', text: 'No. Employer group plans at Lincoln Financial and Parkview Health typically cover only 1–2× annual salary. A Fort Wayne family with a $200,000 mortgage and $70,000 household income needs $700,000–$840,000 in coverage — far beyond what any group plan provides. Private coverage is portable and follows you regardless of employment.' },
        },
        {
          '@type': 'Question',
          name: 'What is the best life insurance for a GM Assembly or manufacturing worker in Fort Wayne?',
          acceptedAnswer: { '@type': 'Answer', text: 'A 20-year term life policy is the most cost-effective choice for Fort Wayne manufacturing workers. It locks in your rate today before any future health changes and stays active regardless of layoffs, plant changes, or retirements. A healthy 35-year-old Fort Wayne worker pays $28–$35/month for $500,000 in coverage.' },
        },
        {
          '@type': 'Question',
          name: 'Can I get life insurance without a medical exam in Fort Wayne?',
          acceptedAnswer: { '@type': 'Answer', text: 'Yes. Many carriers offer no-exam simplified issue policies for healthy Fort Wayne applicants — same-day or next-day approval with no blood draw. Coverage up to $1M is available. Hoosier Life Insurance works with multiple no-exam carriers to find your best option across Allen County.' },
        },
      ],
    };

    const speakableSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${SITE_URL}/life-insurance-fort-wayne#speakable`,
      speakable: { '@type': 'SpeakableSpecification', xpath: ['/html/body//*[@id="fort-wayne-price-summary"]'] },
      url: `${SITE_URL}/life-insurance-fort-wayne`,
    };

    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': ['InsuranceAgency', 'LocalBusiness'],
      '@id': `${SITE_URL}/life-insurance-fort-wayne#localbusiness`,
      name: 'Hoosier Life Insurance — Fort Wayne Indiana',
      url: `${SITE_URL}/life-insurance-fort-wayne`,
      telephone: '+13176482983',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fort Wayne',
        addressRegion: 'IN',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 41.0793,
        longitude: -85.1394,
      },
      areaServed: {
        '@type': 'City',
        name: 'Fort Wayne',
        containedInPlace: { '@type': 'State', name: 'Indiana' },
      },
      description: 'Independent life insurance agency serving Fort Wayne, Indiana. Lincoln Financial employees, GM Assembly workers, Parkview Health staff, and all Allen County families.',
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '09:00', closes: '18:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '10:00', closes: '14:00' },
      ],
      priceRange: '$$',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '63',
        bestRating: '5',
        worstRating: '1',
      },
      sameAs: [
        'https://www.facebook.com/HoosierLifeInsurance',
        'https://twitter.com/HoosierLifeIns',
        'https://www.linkedin.com/company/hoosier-life-insurance',
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Life Insurance Products',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Term Life Insurance', description: '10, 20, and 30-year term policies from 20+ A-rated carriers' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Whole Life Insurance', description: 'Permanent whole life coverage with guaranteed cash value growth' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Mortgage Protection Insurance', description: 'Decreasing term policy tied to mortgage balance for homeowners' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Final Expense Insurance', description: 'Simplified issue whole life covering burial and end-of-life costs' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'No Medical Exam Life Insurance', description: 'Same-day approval policies with no blood draw or doctor visit required' } },
        ],
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${SITE_URL}/life-insurance-fort-wayne#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: `${SITE_URL}/service-areas` },
        { '@type': 'ListItem', position: 3, name: 'Life Insurance Fort Wayne Indiana', item: `${SITE_URL}/life-insurance-fort-wayne` },
      ],
    };

    const schemas = [faqSchema, speakableSchema, localBusinessSchema, breadcrumbSchema];
    schemas.forEach((schema, idx) => {
      const id = `schema-fort-wayne-${idx}`;
      if (document.getElementById(id)) return;
      const el = document.createElement('script');
      el.id = id; el.type = 'application/ld+json'; el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    });
    return () => { schemas.forEach((_, idx) => document.getElementById(`schema-fort-wayne-${idx}`)?.remove()); };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, String(v)));
      body.append('source_page', 'Fort Wayne Life Insurance Landing Page');
      const res = await fetch(FORM_URL, {
        method: 'POST', mode: 'cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) { setSubmitted(true); } else { setError('Something went wrong. Please call (317) 648-2983.'); }
    } catch { setError('Something went wrong. Please call (317) 648-2983.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FB]">
      <title>Life Insurance Fort Wayne Indiana | Compare 20+ Carriers — Hoosier Life Insurance</title>
      <meta name="description" content="Fort Wayne life insurance from an independent agent — compare Lincoln Financial, GM Assembly & Parkview Health supplement options plus 20+ carriers for Allen County families. Free quotes. (317) 648-2983." />
      <link rel="canonical" href="https://hoosierlifeinsurance.com/life-insurance-fort-wayne" />
      <meta name="robots" content="index, follow" />
      <meta property="og:title" content="Life Insurance Fort Wayne Indiana | Compare 20+ Carriers — Hoosier Life Insurance" />
      <meta property="og:url" content="https://hoosierlifeinsurance.com/life-insurance-fort-wayne" />
      <meta name="geo.placename" content="Fort Wayne, Indiana" />
      <meta name="geo.region" content="US-IN" />
      <meta name="geo.position" content="41.0793;-85.1394" />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235] pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D97642]/10 rounded-full blur-[140px] -translate-y-1/3 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#D97642]/8 rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-8">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-white/50">
              <li><Link to="/" className="hover:text-[#D97642] transition-colors">Home</Link></li>
              <li><i className="ri-arrow-right-s-line" aria-hidden="true"></i></li>
              <li><Link to="/service-areas" className="hover:text-[#D97642] transition-colors">Service Areas</Link></li>
              <li><i className="ri-arrow-right-s-line" aria-hidden="true"></i></li>
              <li className="text-[#D97642] font-medium">Life Insurance Fort Wayne</li>
            </ol>
          </nav>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/30 rounded-full px-4 py-1.5 mb-5">
                <i className="ri-map-pin-fill text-[#F0A070] text-[13px]" aria-hidden="true"></i>
                <span className="text-[#F0A070] text-[12px] font-semibold uppercase tracking-widest">Fort Wayne — Allen County · Indiana&apos;s 2nd Largest City</span>
              </div>
              <h1 className="text-[36px] sm:text-[46px] md:text-[52px] font-bold text-white leading-[1.1] mb-4">
                Life Insurance for Fort Wayne Families —<br />
                <span className="text-[#D97642]">Compare 20+ Carriers, Allen County</span><br />
                — Done Right.
              </h1>
              <p className="text-[17px] text-white/80 leading-[1.75] mb-3 max-w-[520px]">
                Lincoln Financial employees. GM Assembly workers. Parkview nurses. Fort Wayne families — we compare 20+ carriers to find your lowest rate. Free, 15 minutes, no pressure.
              </p>
              <p className="text-[15px] text-white/60 leading-[1.7] mb-8 max-w-[480px]">
                I&apos;m Benjamin Wright — an independent Indiana agent. I work for you, not for any insurance company.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { icon: 'ri-building-2-line', text: '20+ Carriers Compared' },
                  { icon: 'ri-time-line', text: '15-Minute Free Call' },
                  { icon: 'ri-map-pin-line', text: 'Serving All Allen County' },
                ].map(b => (
                  <div key={b.text} className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1.5">
                    <div className="w-4 h-4 flex items-center justify-center"><i className={`${b.icon} text-[#D97642] text-[14px]`} aria-hidden="true"></i></div>
                    <span className="text-white/90 text-[13px] font-medium">{b.text}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#fort-wayne-quote-form" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white text-[16px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-search-line text-[18px]" aria-hidden="true"></i>Get My Fort Wayne Rate
                </a>
                <a href="tel:+13176482983" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/15 text-white text-[16px] font-semibold rounded-xl hover:bg-white/25 transition-all border border-white/30 cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-fill text-[18px]" aria-hidden="true"></i>(317) 648-2983
                </a>
              </div>
            </div>
            {/* Right — stats + image */}
            <div>
              <div className="grid grid-cols-2 gap-4 mb-5">
                {[
                  { value: '~270K', label: 'Fort Wayne Residents', sub: 'Indiana\'s 2nd largest city' },
                  { value: '$52K', label: 'Avg Household Income', sub: 'Most families need $500K+ coverage' },
                  { value: '$185K', label: 'Avg Home Value', sub: 'Mortgage needs matching protection' },
                  { value: '20+', label: 'Carriers We Compare', sub: 'You get the best rate available' },
                ].map(s => (
                  <div key={s.label} className="bg-white/10 border border-white/20 rounded-xl p-4">
                    <div className="text-[26px] font-black text-[#D97642] leading-none mb-1">{s.value}</div>
                    <div className="text-[13px] font-semibold text-white leading-tight">{s.label}</div>
                    <div className="text-[11px] text-white/50 mt-0.5">{s.sub}</div>
                  </div>
                ))}
              </div>
              <img
                src="https://public.readdy.ai/ai/img_res/fbbb06347eca3d6e190acb1d759e133f.jpg"
                alt="Fort Wayne Indiana family life insurance"
                className="w-full h-[200px] object-cover object-top rounded-2xl"
                loading="eager"
                decoding="async"
                width={600}
                height={340}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="flex">
              {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-[#F59E0B] text-[18px]" aria-hidden="true"></i>)}
            </div>
            <span className="text-[15px] font-bold text-[#1A3A52]">4.9 / 5</span>
            <span className="text-[13px] text-[#718096]">from 127 Indiana families</span>
          </div>
          <div className="flex items-center gap-6">
            {[['500+','Families Protected'],['$0','Cost to You'],['15 min','Avg Call Time']].map(([val, label]) => (
              <div key={label} className="text-center">
                <div className="text-[22px] font-black text-[#D97642]">{val}</div>
                <div className="text-[11px] text-[#718096] uppercase tracking-wide">{label}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 text-[13px] text-[#4A5568]">
            <i className="ri-shield-check-fill text-[#D97642] text-[16px]" aria-hidden="true"></i>
            <span>Indiana Licensed · <strong>Independent Agent</strong></span>
          </div>
        </div>
      </div>

      {/* Problem Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-3 py-1 mb-4">
                <i className="ri-error-warning-line text-red-500 text-[14px]" aria-hidden="true"></i>
                <span className="text-red-600 text-[12px] font-semibold uppercase tracking-wide">The Coverage Gap Fort Wayne Families Don&apos;t See Coming</span>
              </div>
              <h2 className="text-[30px] md:text-[38px] font-bold text-[#1A3A52] leading-[1.2] mb-5">
                Your Lincoln Financial or GM group plan disappears the day you leave.
              </h2>
              <p className="text-[16px] text-[#4A5568] leading-[1.8] mb-4">
                Employer coverage feels reliable — until a plant restructure, a retirement, or a career change erases it overnight. And even while it&apos;s active, it rarely covers what your family actually needs.
              </p>
              <div className="space-y-3">
                {[
                  'Group plans cover 1–2× salary — rarely enough for a growing family',
                  'Coverage ends the day your employment ends — not a day later',
                  'Rates are never locked — they reset every year as you age',
                  'Your Fort Wayne mortgage is not covered by your employer plan',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-red-100 rounded-full mt-0.5 flex-shrink-0">
                      <i className="ri-close-line text-red-500 text-[12px]" aria-hidden="true"></i>
                    </div>
                    <span className="text-[14px] text-[#4A5568]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 text-white">
              <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/30 rounded-full px-3 py-1 mb-5">
                <i className="ri-check-double-line text-[#D97642] text-[14px]" aria-hidden="true"></i>
                <span className="text-[#F0A070] text-[12px] font-semibold uppercase tracking-wide">The Fix — 15 Minutes</span>
              </div>
              <h3 className="text-[24px] font-bold text-white leading-tight mb-5">What a private policy gives your Fort Wayne family:</h3>
              {[
                { icon: 'ri-home-heart-line', title: 'Pays off your mortgage completely', desc: 'Your family keeps their Fort Wayne home no matter what.' },
                { icon: 'ri-team-line', title: 'Replaces your income for 10–20 years', desc: 'Your spouse doesn\'t face the future alone or scrambling.' },
                { icon: 'ri-graduation-cap-line', title: 'Funds your kids\' education', desc: 'Purdue Fort Wayne, Indiana Tech, Trine — your kids\' choice.' },
                { icon: 'ri-lock-line', title: 'Follows you from job to job', desc: 'Lincoln, GM, Parkview — doesn\'t matter. It\'s yours forever.' },
              ].map(item => (
                <div key={item.title} className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#D97642]/20 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-[#D97642] text-[18px]`} aria-hidden="true"></i>
                  </div>
                  <div>
                    <div className="text-[14px] font-semibold text-white">{item.title}</div>
                    <div className="text-[13px] text-white/60">{item.desc}</div>
                  </div>
                </div>
              ))}
              <a href="#fort-wayne-quote-form" className="block text-center mt-6 py-3.5 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap">
                Get My Free Fort Wayne Rate
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-[#F7F9FB]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-[30px] md:text-[38px] font-bold text-[#1A3A52] mb-3">Life Insurance for Fort Wayne Families</h2>
            <p className="text-[16px] text-[#718096] max-w-[560px] mx-auto">From Aboite to New Haven — every coverage type, every carrier, one independent agent.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map(svc => (
              <Link key={svc.title} to={svc.link} className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-[#D97642] hover:bg-[#FDF6F0] transition-all cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-xl mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${svc.icon} text-white text-[22px]`} aria-hidden="true"></i>
                </div>
                <h3 className="text-[16px] font-bold text-[#1A3A52] mb-2 group-hover:text-[#D97642] transition-colors">{svc.title}</h3>
                <p className="text-[13px] text-[#4A5568] leading-[1.7] mb-4">{svc.desc}</p>
                <div className="flex items-center gap-1 text-[13px] text-[#D97642] font-semibold">
                  <span>{svc.cta}</span>
                  <i className="ri-arrow-right-line text-[14px]" aria-hidden="true"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Table */}
      <section id="fort-wayne-price-summary" className="py-14 bg-white border-y border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-8">
            <h2 className="text-[28px] md:text-[34px] font-bold text-[#1A3A52] mb-2">What Does Life Insurance Cost in Fort Wayne, Indiana?</h2>
            <p className="text-[15px] text-[#718096] max-w-[620px] mx-auto">Rates for healthy, non-smoking Fort Wayne residents. Indiana rates run 8–12% below the national average.</p>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white mb-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1A3A52] text-white">
                  {['Age', 'Coverage', 'Term', 'Male/Month', 'Female/Month'].map(h => (
                    <th key={h} className="px-5 py-3.5 text-left font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { age: '30', cov: '$250,000', term: '20-year', m: '$16', f: '$14' },
                  { age: '30', cov: '$500,000', term: '20-year', m: '$28', f: '$23' },
                  { age: '35', cov: '$500,000', term: '20-year', m: '$33', f: '$28' },
                  { age: '35', cov: '$750,000', term: '20-year', m: '$48', f: '$40' },
                  { age: '40', cov: '$500,000', term: '20-year', m: '$47', f: '$39' },
                  { age: '45', cov: '$500,000', term: '20-year', m: '$69', f: '$57' },
                  { age: '50', cov: '$250,000', term: '20-year', m: '$56', f: '$46' },
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                    <td className="px-5 py-3 font-semibold text-[#1A3A52]">{row.age}</td>
                    <td className="px-5 py-3 text-[#4A5568]">{row.cov}</td>
                    <td className="px-5 py-3 text-[#4A5568]">{row.term}</td>
                    <td className="px-5 py-3 font-bold text-[#D97642]">{row.m}/mo</td>
                    <td className="px-5 py-3 font-bold text-[#D97642]">{row.f}/mo</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[12px] text-[#718096] italic">*Estimated rates for healthy non-smokers at Preferred Plus classification. Actual rates vary by carrier and health profile.</p>
            <a href="#fort-wayne-quote-form" className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97642] text-white text-[14px] font-bold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-[16px]" aria-hidden="true"></i>Get My Exact Fort Wayne Rate
            </a>
          </div>
        </div>
      </section>

      {/* Quote Form + Testimonials */}
      <section id="fort-wayne-quote-form" className="py-16 md:py-24 bg-[#FDF6F0]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Testimonials */}
            <div className="lg:col-span-2 flex flex-col gap-4">
              <div className="mb-2">
                <h2 className="text-[24px] font-bold text-[#1A3A52] mb-2">What Fort Wayne Families Are Saying</h2>
                <p className="text-[14px] text-[#718096]">Real people. Real Allen County neighborhoods. No fluff.</p>
              </div>
              {TESTIMONIALS.map((t, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center gap-1 mb-2">
                    {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-[#F59E0B] text-[14px]" aria-hidden="true"></i>)}
                  </div>
                  <p className="text-[14px] text-[#4A5568] leading-[1.7] mb-3 italic">&ldquo;{t.text}&rdquo;</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[13px] font-bold text-[#1A3A52]">{t.name}</div>
                      <div className="text-[12px] text-[#718096]">{t.neighborhood}</div>
                    </div>
                    <span className="text-[11px] bg-[#D97642]/10 text-[#D97642] font-semibold px-2 py-1 rounded-full whitespace-nowrap">{t.product}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-gray-200 p-8">
                <div className="mb-6">
                  <div className="inline-flex items-center gap-2 bg-[#D97642]/10 border border-[#D97642]/20 rounded-full px-3 py-1 mb-3">
                    <i className="ri-map-pin-line text-[#D97642] text-[13px]" aria-hidden="true"></i>
                    <span className="text-[#D97642] text-[12px] font-semibold">Serving All Fort Wayne &amp; Allen County</span>
                  </div>
                  <h2 className="text-[24px] font-bold text-[#1A3A52] mb-1">Get Your Free Fort Wayne Rate</h2>
                  <p className="text-[14px] text-[#718096]">Takes 2 minutes. No obligation. We respond within 24 hours.</p>
                </div>

                {submitted ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                      <i className="ri-check-double-line text-green-600 text-[32px]" aria-hidden="true"></i>
                    </div>
                    <h3 className="text-[20px] font-bold text-[#1A3A52] mb-2">You&apos;re all set!</h3>
                    <p className="text-[14px] text-[#4A5568] mb-6">We&apos;ll reach out within 24 hours with your Fort Wayne-specific options from 20+ carriers.</p>
                    <button onClick={() => setSubmitted(false)} className="px-6 py-3 bg-[#D97642] text-white font-semibold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap text-[14px]">
                      Submit Another Request
                    </button>
                  </div>
                ) : (
                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <input type="hidden" name="source_page" value="Fort Wayne Life Insurance Landing Page" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">First Name <span className="text-[#D97642]">*</span></label>
                        <input type="text" name="first_name" required placeholder="Jane" value={formData.first_name} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Last Name <span className="text-[#D97642]">*</span></label>
                        <input type="text" name="last_name" required placeholder="Smith" value={formData.last_name} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Email Address <span className="text-[#D97642]">*</span></label>
                      <input type="email" name="email" required placeholder="jane@example.com" value={formData.email} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Phone Number <span className="text-[#D97642]">*</span></label>
                      <input type="tel" name="phone" required placeholder="(260) 555-0100" value={formData.phone} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Your Age <span className="text-[#D97642]">*</span></label>
                        <select name="age" required value={formData.age} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] cursor-pointer bg-white">
                          <option value="">Select</option>
                          {['18–25','26–30','31–35','36–40','41–45','46–50','51–55','56–60','61–65','65+'].map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Coverage Amount</label>
                        <select name="coverage_amount" value={formData.coverage_amount} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] cursor-pointer bg-white">
                          <option value="">Not sure</option>
                          {['$100K–$250K','$250K–$500K','$500K–$750K','$750K–$1M','$1M+'].map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Best Time to Call</label>
                      <select name="best_time" value={formData.best_time} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] cursor-pointer bg-white">
                        <option value="">Anytime</option>
                        {['Morning (8am–12pm)','Afternoon (12pm–5pm)','Evening (5pm–9pm)','Weekend'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    {error && (
                      <p className="text-[13px] text-red-500 flex items-center gap-1.5">
                        <i className="ri-error-warning-line text-[15px]" aria-hidden="true"></i>{error}
                      </p>
                    )}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" name="sms_consent" required checked={formData.sms_consent} onChange={handleChange} className="mt-0.5 w-4 h-4 accent-[#D97642] flex-shrink-0 cursor-pointer" />
                        <span className="text-[12px] text-gray-600 leading-relaxed">
                          <strong className="text-gray-800">SMS Consent (Required):</strong> I agree to receive text messages from Hoosier Life Insurance about my quote. Reply STOP anytime.{' '}
                          <a href="/privacy-policy" className="text-[#D97642] underline">Privacy Policy</a>
                        </span>
                      </label>
                    </div>
                    <button
                      type="submit"
                      disabled={submitting || !formData.sms_consent}
                      className="w-full py-4 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <><i className="ri-loader-4-line animate-spin text-[18px]" aria-hidden="true"></i> Sending...</>
                      ) : (
                        <><i className="ri-send-plane-fill text-[16px]" aria-hidden="true"></i> Show Me My Fort Wayne Rate — Free</>
                      )}
                    </button>
                    <p className="text-[11px] text-[#718096] text-center">No obligation. We never sell your information.</p>
                  </form>
                )}
              </div>
              <div className="mt-4 flex items-center gap-4 bg-[#1A3A52] rounded-xl p-5">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-full flex-shrink-0">
                  <i className="ri-phone-fill text-white text-[20px]" aria-hidden="true"></i>
                </div>
                <div>
                  <div className="text-[12px] text-white/60 uppercase tracking-wider font-semibold">Prefer to call directly?</div>
                  <a href="tel:+13176482983" className="text-[22px] font-bold text-white hover:text-[#D97642] transition-colors cursor-pointer">(317) 648-2983</a>
                  <div className="text-[12px] text-white/60">Mon–Fri 9AM–6PM · Sat 10AM–2PM · Evenings by appointment</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Employers */}
      <section className="py-14 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-8">
            <h2 className="text-[28px] md:text-[34px] font-bold text-[#1A3A52] mb-2">Fort Wayne&apos;s Major Employers — And Why Their Group Plans Fall Short</h2>
            <p className="text-[15px] text-[#718096] max-w-[580px] mx-auto">Work here? Your employer plan is a starting point — not a complete strategy for your family.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {EMPLOYERS.map((e, i) => (
              <div key={i} className="bg-[#F7F9FB] rounded-xl p-5 border border-gray-100 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#1A3A52]/10 rounded-lg flex-shrink-0">
                  <i className="ri-building-line text-[#1A3A52] text-xl" aria-hidden="true"></i>
                </div>
                <div>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                    <span className="font-bold text-[#1A3A52] text-[15px]">{e.employer}</span>
                    <span className="text-[11px] text-[#718096] bg-gray-200 px-2 py-0.5 rounded-full">{e.employees}</span>
                  </div>
                  <p className="text-[13px] text-[#718096] leading-relaxed">{e.gap}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="py-14 bg-[#F7F9FB]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <h2 className="text-[28px] font-bold text-[#1A3A52] mb-6 text-center">We Serve All Fort Wayne Neighborhoods &amp; Allen County</h2>
          <div className="flex flex-wrap gap-2 justify-center">
            {NEIGHBORHOODS.map(n => (
              <div key={n} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-[#D97642]/20 rounded-lg text-[13px] font-medium text-[#1A3A52]">
                <i className="ri-map-pin-line text-[#D97642] text-[14px]" aria-hidden="true"></i>
                {n}
              </div>
            ))}
          </div>
          <p className="text-[14px] text-[#718096] text-center mt-6">
            Serving all Fort Wayne ZIP codes: 46801–46809, 46814–46816, 46818–46819, 46825, 46835. All consultations by phone or Zoom.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[800px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-[30px] md:text-[38px] font-bold text-[#1A3A52] mb-3">Fort Wayne Life Insurance FAQ</h2>
            <p className="text-[15px] text-[#718096]">The questions Allen County families ask us most.</p>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className={`rounded-xl overflow-hidden border transition-all ${openFaq === i ? 'border-[#D97642]/50 bg-white' : 'border-gray-200 bg-white hover:border-[#D97642]/30'}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer gap-3"
                  aria-expanded={openFaq === i}
                >
                  <span className="text-[15px] font-semibold text-[#1A3A52] leading-snug">{faq.q}</span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${openFaq === i ? 'bg-[#D97642] rotate-180' : 'bg-gray-100'}`}>
                    <i className={`ri-arrow-down-s-line text-[18px] ${openFaq === i ? 'text-white' : 'text-gray-500'}`} aria-hidden="true"></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-[14px] text-[#4A5568] leading-[1.8]">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Hub Callout */}
      <section className="py-9 bg-[#0F2A3F]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white/5 border border-white/10 rounded-2xl px-7 py-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-xl flex-shrink-0">
                <i className="ri-book-open-line text-white text-[22px]" aria-hidden="true"></i>
              </div>
              <div>
                <p className="text-[11px] text-[#D97642] font-bold uppercase tracking-widest mb-1">Indiana Life Insurance Learning Hub</p>
                <h3 className="text-[19px] font-bold text-white leading-tight mb-1">Not sure where to start with coverage?</h3>
                <p className="text-[14px] text-white/65">27 Indiana guides + 5 pillar posts + 7 city breakdowns — all in one place.</p>
              </div>
            </div>
            <Link to="/learning-hub" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap flex-shrink-0">
              <i className="ri-compass-3-line text-[17px]" aria-hidden="true"></i>
              Explore the Learning Hub
              <i className="ri-arrow-right-line text-[15px]" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F]">
        <div className="max-w-[800px] mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-[30px] md:text-[38px] font-bold text-white mb-4">
            Fort Wayne families deserve the best rate — not the first one.
          </h2>
          <p className="text-[17px] text-white/75 leading-[1.7] mb-8 max-w-[580px] mx-auto">
            15 minutes, zero pressure, and you&apos;ll have rates from 20+ carriers to choose from.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#fort-wayne-quote-form" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white text-[16px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-[18px]" aria-hidden="true"></i>Get My Free Rate Now
            </a>
            <Link to="/book-appointment" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/15 text-white text-[16px] font-semibold rounded-xl hover:bg-white/25 transition-all border border-white/30 cursor-pointer whitespace-nowrap">
              <i className="ri-calendar-check-line text-[18px]" aria-hidden="true"></i>Schedule a Call
            </Link>
          </div>
          <p className="text-[13px] text-white/40 mt-6">Serving Fort Wayne, Aboite, Waynedale, New Haven, Leo-Cedarville &amp; all of Allen County</p>
        </div>
      </section>

      {/* Cross-links */}
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-4">Also Serving Indiana</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: 'Life Insurance Indianapolis', href: '/life-insurance-indianapolis', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance South Bend', href: '/life-insurance-south-bend', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Evansville', href: '/life-insurance-evansville', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Indiana (Statewide)', href: '/life-insurance-indiana', icon: 'ri-map-2-line' },
              { label: 'No Medical Exam Options', href: '/no-medical-exam', icon: 'ri-stethoscope-line' },
              { label: 'Service Areas — All Indiana', href: '/service-areas', icon: 'ri-compass-3-line' },
            ].map(link => (
              <Link key={link.label} to={link.href} className="flex items-center gap-2 px-4 py-2 bg-[#F7F9FB] border border-gray-200 rounded-lg text-[13px] font-medium text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${link.icon} text-[#D97642] text-[14px]`} aria-hidden="true"></i></div>
                {link.label}
              </Link>
            ))}
          </div>
          {/* Blog articles — topical authority */}
          <div className="mt-5 pt-5 border-t border-gray-100">
            <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-4">Learn More From Our Blog</p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { label: 'How Much Life Insurance Does an Indiana Family Need?', href: '/blog/how-much-life-insurance-indiana' },
                { label: 'No Medical Exam Life Insurance in Indiana', href: '/blog/no-medical-exam-life-insurance-indiana' },
                { label: 'Term Life Insurance in Indianapolis & Indiana', href: '/blog/term-life-insurance-indianapolis' },
                { label: 'Life Insurance for Indiana Families', href: '/blog/life-insurance-for-families-indianapolis' },
                { label: 'Life Insurance for the Self-Employed in Indiana', href: '/blog/life-insurance-self-employed-indiana' },
              ].map(blog => (
                <Link key={blog.href} to={blog.href} className="flex items-center gap-2 px-4 py-2 bg-[#FDF6F0] border border-[#D97642]/20 rounded-lg text-[13px] text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-colors cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-article-line text-[#D97642] text-[14px]" aria-hidden="true"></i></div>
                  {blog.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
